import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Users, Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios, { API } from "@/lib/api";
import EmptyState from "@/components/shared/EmptyState";
import LoadingScreen from "@/components/shared/LoadingScreen";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}

export default function ProgramList() {
  const { type } = useParams();
  const [programs, setPrograms] = useState(null);
  const [search, setSearch] = useState("");
  const [activeType, setActiveType] = useState(type || "all");
  const navigate = useNavigate();

  useEffect(() => {
    setPrograms(null);
    const params = {};
    if (activeType !== "all") params.type = activeType;
    if (search) params.search = search;
    axios.get(`${API}/public/programs`, { params }).then(({ data }) => setPrograms(data)).catch(() => setPrograms([]));
  }, [activeType, search]);

  return (
    <div data-testid="program-list-page">
      <section className="bg-gradient-to-br from-[#084C3A] to-[#0B4F71] px-4 py-16 text-center sm:px-6">
        <h1 className="font-heading text-3xl font-bold text-white sm:text-4xl">Program Haji &amp; Umrah</h1>
        <p className="mt-3 text-white/80">Temukan program yang sesuai kebutuhan dan kesiapan Anda.</p>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList data-testid="program-type-tabs" className="rounded-full bg-[#F5F9F7]">
              <TabsTrigger value="all" data-testid="program-tab-all" className="rounded-full">Semua</TabsTrigger>
              <TabsTrigger value="haji" data-testid="program-tab-haji" className="rounded-full">Haji</TabsTrigger>
              <TabsTrigger value="umrah" data-testid="program-tab-umrah" className="rounded-full">Umrah</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
            <Input
              data-testid="program-search-input"
              placeholder="Cari program..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 rounded-full pl-9"
            />
          </div>
        </div>

        {programs === null ? (
          <LoadingScreen />
        ) : programs.length === 0 ? (
          <div className="mt-10"><EmptyState title="Program tidak ditemukan" description="Coba ubah kata kunci pencarian atau filter jenis program." /></div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <div key={p.id} data-testid={`program-list-card-${p.id}`} className="overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(15,107,79,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(15,107,79,0.14)]">
                <div className="relative h-48">
                  <img src={p.cover_image} alt={p.name} className="h-full w-full object-cover" />
                  <Badge className="absolute left-3 top-3 rounded-full bg-white/95 text-[#0F6B4F] capitalize">{p.type}</Badge>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-[#1F2937] line-clamp-1">{p.name}</h3>
                  <div className="mt-3 space-y-1.5 text-sm text-[#64748B]">
                    <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {formatDate(p.departure_date)}</p>
                    <p className="flex items-center gap-2"><Users className="h-4 w-4" /> Sisa Kuota: {Math.max(p.quota - p.quota_taken, 0)} / {p.quota}</p>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-[#92720C]"><Sparkles className="h-3.5 w-3.5" /> Info Harga via Admin</p>
                    <Button size="sm" data-testid={`program-list-detail-button-${p.id}`} onClick={() => navigate(`/program/${p.slug}`)} className="rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">
                      Lihat Detail
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
